import { useState, useEffect } from 'react';
import { hasPermission, carregarPermissoes } from '../../utils/permissions';

/**
 * Hook para verificar permissões do usuário logado
 * 
 * @returns {Object} Objeto com funções para verificar permissões
 * 
 * @example
 * // Em um componente:
 * const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando } = usePermissions('despesas');
 * 
 * if (carregando) return <div>Carregando...</div>;
 * if (!podeAbrir) return <div>Sem permissão para acessar este módulo</div>;
 * 
 * return (
 *   <div>
 *     <button disabled={!podeCriar}>Novo</button>
 *     <button disabled={!podeEditar}>Editar</button>
 *     <button disabled={!podeExcluir}>Excluir</button>
 *     <button disabled={!podeExportar}>Exportar</button>
 *   </div>
 * );
 */
export default function usePermissions(modulo) {
  const [carregando, setCarregando] = useState(true);
  const [permissoes, setPermissoes] = useState({
    abrir: true,
    criar: true,
    editar: true,
    excluir: true,
    exportar: true,
  });

  useEffect(() => {
    async function carregar() {
      try {
        setCarregando(true);
        
        // Pega dados do usuário e associação do localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        const associacao = JSON.parse(localStorage.getItem('associacao'));
        
        if (!user?.cpf || !associacao?.cnpj) {
          console.warn('⚠️ Usuário ou associação não encontrados no localStorage');
          // Mantém permissões padrão (todos true)
          setCarregando(false);
          return;
        }

        // Carrega permissões do backend
        await carregarPermissoes(user.cpf, associacao.cnpj);

        // Verifica cada permissão
        const [abrir, criar, editar, excluir, exportar] = await Promise.all([
          hasPermission(user.cpf, associacao.cnpj, modulo, 'abrir'),
          hasPermission(user.cpf, associacao.cnpj, modulo, 'criar'),
          hasPermission(user.cpf, associacao.cnpj, modulo, 'editar'),
          hasPermission(user.cpf, associacao.cnpj, modulo, 'excluir'),
          hasPermission(user.cpf, associacao.cnpj, modulo, 'exportar'),
        ]);

        setPermissoes({ abrir, criar, editar, excluir, exportar });
      } catch (error) {
        console.error('❌ Erro ao carregar permissões:', error);
        // Em caso de erro, mantém permissões padrão (todos true)
      } finally {
        setCarregando(false);
      }
    }

    carregar();
  }, [modulo]);

  return {
    carregando,
    podeAbrir: permissoes.abrir,
    podeCriar: permissoes.criar,
    podeEditar: permissoes.editar,
    podeExcluir: permissoes.excluir,
    podeExportar: permissoes.exportar,
    permissoes, // Retorna objeto completo para casos especiais
  };
}
